<script setup lang="ts">
import {onMounted} from 'vue';
import type {Ref} from 'vue';
import type {IApartment, IPlan} from '~/modules/RealEstate/api';
import {useLocale} from '~/plugins/Localization';
import {
  getApartamentsForFloor,
  getMemoizeApartment,
  memoizeApartment,
} from '~/modules/RealEstate/controller';

const props = defineProps({
  plan: {type: Array as () => IPlan},
});
const emits = defineEmits([
  'mousemoveApartment',
  'mouseleaveApartment',
  'openModalApartment',
  'apartmentUploaded',
]);

const currentApartaments: Ref<IApartment[]> = ref([]);
const locale = useLocale();
function translate(name: string) {
  const word = name.split(' ');
  const translated = locale.getRealty(word[0]);
  return translated !== `${name}_No_translated`
    ? translated + ' ' + word[1]
    : name;
}
const mousemoveHandler = (e: Event, data: any) => {
  emits('mousemoveApartment', e, data);
};
const mouseleaveHandler = () => emits('mouseleaveApartment');
const openModal = (room: IApartment) =>
  emits('openModalApartment', room);
onMounted(async () => {
  if (getMemoizeApartment(props.plan.floors[0].uuid)?.length !== 0) {
    currentApartaments.value = getMemoizeApartment(
      props.plan.floors[0].uuid,
    );
  } else {
    currentApartaments.value = await getApartamentsForFloor(
      props.plan.floors[0].uuid,
    );
    memoizeApartment(currentApartaments.value);
  }
  window.addEventListener('resize', () => recalculatePolyline());
  await getImageWH();
  recalculatePolyline();
});
function recalculatePolyline() {
  const objectPlanBlock = document.querySelectorAll('.genplan');
  objectPlanBlock.forEach((object) => {
    const objectPlanBuildings = object.querySelectorAll(
      '.genplan__buildings-item',
    );
    const objectPlanPicture: any =
      object.querySelector('.genplan-img');

    async function calcCurrentPoints() {
      await nextTick();

      const objectPlanPictureWidthOrigin =
        objectPlanPicture.dataset.originWidth;
      const objectPlanPictureHeightOrigin =
        objectPlanPicture.dataset.originHeight;
      const objectPlanPictureWidthCurrent =
        objectPlanPicture.width || objectPlanPicture.clientWidth;
      const objectPlanPictureHeightCurrent =
        objectPlanPicture.height || objectPlanPicture.clientHeight;

      const coefficientWidth =
        objectPlanPictureWidthCurrent / objectPlanPictureWidthOrigin;
      const coefficientHeight =
        objectPlanPictureHeightCurrent /
        objectPlanPictureHeightOrigin;

      objectPlanBuildings.forEach((item: any, index) => {
        const originPoints = item.dataset.originPoints.trim();
        const parseOriginPoints = originPoints.split(' ');
        let newPoints = '';
        parseOriginPoints.forEach((point: any) => {
          const parsePoint = point.split(',');
          newPoints +=
            parsePoint[0] * coefficientWidth +
            ',' +
            parsePoint[1] * coefficientHeight +
            ' ';
        });
        // console.log(newPoints);
        item.setAttribute('points', newPoints.toString());
        item.style.display = 'block';
      });
    }

    if (objectPlanPicture) {
      if (objectPlanPicture.complete) {
        calcCurrentPoints();
      } else {
        objectPlanPicture.onload = calcCurrentPoints;
      }
    }
  });
}
const getImageWH = async () => {
  await nextTick();
  // @typescript-eslint/no-unused-vars
  return new Promise<void>((resolve, reject) => {
    const images: any = document.querySelectorAll('.genplan-img');
    // console.log(images);
    const imagesArray = Array.from(images);
    const promises = imagesArray.map((image: any) => {
      return new Promise<void>((resolve, reject) => {
        let width;
        let height;
        const img = new Image();
        img.src = image.src;
        img.onload = () => {
          width = img.width;
          height = img.height;
          // console.log({w: width, h: height});
          image.setAttribute('data-origin-width', width);
          image.setAttribute('data-origin-height', height);
          resolve();
        };
      });
    });
    Promise.all(promises).then(() => resolve());
  });
};
</script>

<template>
  <div class="genplan">
    <img
      v-if="currentApartaments.length !== 0"
      ref="genplanImgRef"
      :src="plan.preview ? plan.preview : ''"
      class="genplan-img"
      alt="No preview"
    />
    <div
      v-if="currentApartaments.length === 0"
      class="preload center"
    />
    <svg class="genplan__buildings">
      <polyline
        v-for="room in currentApartaments"
        :key="room.apartment_type.entrance?.points"
        class="genplan__buildings-item"
        :data-uuid="room.uuid"
        :data-origin-points="room.apartment_type.entrance?.points"
        :data-title="translate(room.name)"
        :data-square="room.properties.square"
        points=""
        @mousemove="
          mousemoveHandler($event, {
            title: translate(room.name),
            square: room.properties.square,
            price: room.apartment_type.entrance?.modal?.price,
          })
        "
        @click="openModal(room)"
        @mouseleave="mouseleaveHandler"
      />
    </svg>
  </div>
</template>

<style scoped lang="scss">
.genplan {
  position: relative;
  .preload {
    border-radius: 16px;
    &.center {
      background: linear-gradient(
        107deg,
        #f6f8fd 36.98%,
        #e1e5f0 49.48%,
        #f6f8fd 61.46%
      );
      animation: 3s shine linear infinite;
      background-size: 200% 100%;
      height: 500px;
      width: 500px;
      margin-top: 10px;
    }
  }
  &-img {
    width: 100%;
    max-height: 100vh;
  }
  &__buildings {
    position: absolute;
    z-index: 5;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__buildings-item {
    display: flex;
    position: relative;
    fill: #ffffff00;
    opacity: 0.5;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
      fill: #77bbf0;
    }

    &.active {
      fill: #77bbf0;
    }
  }
}
@keyframes shine {
  to {
    background-position-x: -200%;
  }
}
</style>
